// WEB-2142: клиент импортируется по алиасу '@/api/client', а не './client'.
// Шрам scars/api.md (05.09): относительный путь не подменяется в тестах, и
// любой набор, чей граф импорта задевает этот модуль, падает на разборе
// import.meta внутри настоящего client.ts. Тот же файл, другой путь.
import { api } from '@/api/client';
import type { components } from './generated/schema';
// WEB-2142: страница истории версий одна на квиз и пейволл — окно истории в
// конструкторе общее, и разные размеры страницы вели бы себя по-разному в
// одном и том же окне.
import type { VersionsPageParams } from './quizzes';

/**
 * WEB-745: server-side pagination + search for GET /projects/:id/paywalls.
 * limit/offset mirror backend PaginationQueryDto (limit 1..100, default 20;
 * offset default 0). `state` is the pre-existing filter, now forwarded as a
 * real query param instead of client-side-only.
 */
export interface PaywallListParams {
  limit?: number;
  offset?: number;
  search?: string;
  state?: string;
}

/** WEB-745: lightweight option row for filter dropdowns — GET .../paywalls/options. */
export interface PaywallOptionItem {
  id: string;
  name: string;
  slug: string;
  /**
   * Ревизия 2 WEB-1152 (дефект 3): состояние пейволла. Нужно селектору
   * портал-пейволла (назначить можно только опубликованный); фильтры
   * статистики и лидов кормятся тем же полным списком и его игнорируют.
   * Optional — старый бэкенд поля не отдаёт.
   */
  state?: string;
}

export const paywallsApi = {
  list: (projectId: string, params?: PaywallListParams) =>
    api.get(`/projects/${projectId}/paywalls`, { params }),
  // WEB-745: unpaginated {id,name,slug} list for filter dropdowns (BE now
  // caps GET / above at limit=20 by default — dropdowns need every paywall,
  // not just the first page).
  listOptions: (projectId: string) =>
    api.get<PaywallOptionItem[]>(`/projects/${projectId}/paywalls/options`),
  create: (projectId: string, data: Record<string, unknown>) =>
    api.post(`/projects/${projectId}/paywalls`, data),
  get: (projectId: string, id: string) =>
    api.get(`/projects/${projectId}/paywalls/${id}`),
  update: (projectId: string, id: string, data: Record<string, unknown>) =>
    api.patch(`/projects/${projectId}/paywalls/${id}`, data),
  publish: (projectId: string, id: string) =>
    api.post(`/projects/${projectId}/paywalls/${id}/publish`),
  unpublish: (projectId: string, id: string) =>
    api.post(`/projects/${projectId}/paywalls/${id}/unpublish`),
  duplicate: (projectId: string, id: string) =>
    api.post(`/projects/${projectId}/paywalls/${id}/duplicate`),
  delete: (projectId: string, id: string) =>
    api.delete(`/projects/${projectId}/paywalls/${id}`),
  // WEB-378: how many ACTIVE funnels will break if this paywall is deleted.
  dependentFunnels: (projectId: string, id: string) =>
    api.get<{ activeFunnels: number }>(
      `/projects/${projectId}/paywalls/${id}/dependent-funnels`,
    ),
  /**
   * WEB-2142 (зеркало квиза): история версий грузится СТРАНИЦЕЙ и без тел
   * снимков — ответ `{ versions, total }`.
   */
  getVersions: (projectId: string, id: string, params?: VersionsPageParams) =>
    api.get(`/projects/${projectId}/paywalls/${id}/versions`, { params }),
  /**
   * WEB-2548 (зеркало квиза): одна версия с телом снимка — для «Посмотреть»
   * в окне истории. Сервер отдаёт версию любого вида (публикация или
   * тестовая) с отметкой очистки тела `bodyPurgedAt`.
   */
  getVersion: (projectId: string, id: string, versionId: string) =>
    api.get<components['schemas']['PaywallVersionResponseDto']>(
      `/projects/${projectId}/paywalls/${id}/versions/${versionId}`,
    ),

  /**
   * WEB-2651: `baseUpdatedAt` — отметка версии, которую видит редактор (как у
   * сохранения черновика): сервер сверяет её и отвечает 409 STALE_DRAFT, если
   * страницу успели поменять. Нет отметки — ключа в теле нет, как раньше.
   */
  rollback: (projectId: string, id: string, versionId: string, baseUpdatedAt?: string) =>
    api.post(`/projects/${projectId}/paywalls/${id}/rollback`, {
      versionId,
      ...(baseUpdatedAt !== undefined && { baseUpdatedAt }),
    }),
  /**
   * WEB-2138 ч.1 (сервер — WEB-2139, зеркало квиза): «На проверку» —
   * зафиксировать черновик пейволла тестовой версией. `baseUpdatedAt`
   * обязательна (расхождение → 409 STALE_DRAFT). Ответ `{ version, paywall }`.
   */
  createTestVersion: (projectId: string, id: string, baseUpdatedAt: string) =>
    api.post<components['schemas']['PaywallTestVersionResponseDto']>(
      `/projects/${projectId}/paywalls/${id}/test-versions`,
      { baseUpdatedAt } satisfies components['schemas']['CreateTestVersionDto'],
    ),
  /**
   * WEB-4172: «Сохранить версию» — снимок черновика видом `saved` без
   * публикации (сервер — WEB-4120). Метка обязательна (строгая сверка:
   * расхождение → 409 STALE_DRAFT); подпись — только если есть. Строку куска
   * сервер не меняет, ответ — `{ version }`.
   */
  createSavedVersion: (
    projectId: string,
    id: string,
    baseUpdatedAt: string,
    label?: string,
  ) =>
    api.post<components['schemas']['PaywallSavedVersionResponseDto']>(
      `/projects/${projectId}/paywalls/${id}/saved-versions`,
      {
        baseUpdatedAt,
        ...(label !== undefined && { label }),
      } satisfies components['schemas']['CreateSavedVersionDto'],
    ),
  /**
   * WEB-4120: «Вернуть в черновик» сохранённую версию (вид `saved`) — без
   * публикации. `baseUpdatedAt` обязательна (строгая сверка: расхождение →
   * 409 STALE_DRAFT). Ответ — пейволл со свежим `reviewState`.
   */
  restoreDraft: (
    projectId: string,
    id: string,
    versionId: string,
    baseUpdatedAt: string,
  ) =>
    api.post<components['schemas']['PaywallResponseDto']>(
      `/projects/${projectId}/paywalls/${id}/restore-draft`,
      {
        versionId,
        baseUpdatedAt,
      } satisfies components['schemas']['RestoreDraftDto'],
    ),
  move: (projectId: string, id: string, targetProjectId: string) =>
    api.post(`/projects/${projectId}/paywalls/${id}/move`, { targetProjectId }),
  copy: (projectId: string, id: string, targetProjectId: string) =>
    api.post(`/projects/${projectId}/paywalls/${id}/copy`, { targetProjectId }),
};
